// ─── Impostazioni → Utenti staff ─────────────────────────────────────────────
//
// 🩹 È LA SCHERMATA DOVE SI VEDE SE IL DIFETTO DEL § 01.2 È STATO CURATO.
// Nel gestionale di oggi, spuntando un capitolo le sottosezioni NON si
// attivano: il committente ha dovuto metterle a mano una per una. Qui la
// spunta del capitolo le porta con sé, e toglierla se le riporta via.
//
// ⚖️ E la stessa regola vale sul SERVER (`normalizzaPermessi`), non solo qui:
// se stesse solo a schermo, una chiamata fatta da un'altra parte scriverebbe
// una sottosezione senza il suo capitolo — cioè un permesso su una schermata a
// cui non si arriva.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CAPITOLI,
  ETICHETTE,
  RUOLI,
  concediCapitolo,
  normalizzaPermessi,
  togliCapitolo,
  type Permesso,
  type Ruolo,
  type UtenteStaff,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { useSessione } from '../../features/auth/sessione.js';
import { Attesa, Campo, Guasto, Vuoto } from '../../components/pezzi.js';

export function Utenti(): React.JSX.Element {
  const { profilo } = useSessione();
  // ⚖️ Due condizioni, e la seconda non è ridondante: un `readonly` può avere
  // `manage_users` spuntato — le spunte e il ruolo sono due assi diversi — ma
  // il server gli rifiuta OGNI scrittura (`SOLA_LETTURA`). Mostrargli il
  // bottone «Salva» vorrebbe dire offrirgli un gesto che finisce in un errore.
  // 📌 Il freno vero resta quello del server: qui si evita di prendere in giro
  //    chi guarda, non si difende niente.
  const puòModificare =
    (profilo?.permissions.includes('manage_users') ?? false) && profilo?.role !== 'readonly';
  const [aperto, setAperto] = useState<string | null>(null);

  const elenco = useQuery({
    queryKey: ['utenti-staff'],
    queryFn: () => chiama<{ items: UtenteStaff[] }>('GET', '/settings/users'),
  });

  if (elenco.isLoading) return <Attesa cosa="gli operatori" />;
  if (elenco.error) return <Guasto errore={elenco.error} />;

  const items = elenco.data?.items ?? [];

  return (
    <section>
      <header className="testata">
        <h2>Utenti staff</h2>
        <p className="tenue">
          {items.length} operatori. {puòModificare ? '' : 'Questo accesso può guardare, non cambiare.'}
        </p>
      </header>

      {items.length === 0 ? (
        <Vuoto titolo="Nessun operatore" perché="Non è mai stato creato nessun accesso." />
      ) : (
        <div className="tabella-scorre">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Nome</th>
                <th>Ruolo</th>
                <th>Accesso</th>
                <th>Ultimo accesso</th>
                <th>Permessi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <Riga
                  key={u.id}
                  utente={u}
                  io={u.id === profilo?.id}
                  modificabile={puòModificare}
                  aperto={aperto === u.id}
                  apri={() => setAperto(aperto === u.id ? null : u.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {puòModificare && <Nuovo />}
    </section>
  );
}

function Riga({
  utente,
  io,
  modificabile,
  aperto,
  apri,
}: {
  utente: UtenteStaff;
  io: boolean;
  modificabile: boolean;
  aperto: boolean;
  apri: () => void;
}): React.JSX.Element {
  const qc = useQueryClient();
  const aggiorna = useMutation({
    meta: {
      fatto: (_d, c) => {
        const v = c as { role?: Ruolo; active?: boolean };
        if (v.active === false) return `accesso di ${utente.email} sospeso`;
        if (v.active === true) return `accesso di ${utente.email} riattivato`;
        return `ruolo di ${utente.email} cambiato`;
      },
    },
    mutationFn: (c: { role?: Ruolo; active?: boolean }) => chiama('PATCH', `/settings/users/${utente.id}`, c),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['utenti-staff'] }),
  });

  return (
    <>
      <tr className={utente.active ? '' : 'spento'}>
        <td>
          {utente.email}
          {io && <span className="etichetta"> tu</span>}
        </td>
        <td>{utente.fullName ?? '—'}</td>
        <td>
          <select
            value={utente.role}
            disabled={!modificabile || io || aggiorna.isPending}
            onChange={(e) => aggiorna.mutate({ role: e.target.value as Ruolo })}
          >
            {RUOLI.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </td>
        <td>{utente.hasPassword ? 'creato' : 'da creare'}</td>
        <td>{utente.lastLoginAt ? new Date(utente.lastLoginAt).toLocaleString('it-IT') : 'mai'}</td>
        <td>
          <button type="button" className="piatto" onClick={apri}>
            {utente.permissions.length} spunte {aperto ? '▾' : '▸'}
          </button>
        </td>
      </tr>
      {aperto && (
        <tr>
          <td colSpan={6}>
            <Spunte utente={utente} modificabile={modificabile} />
            {modificabile && !io && (
              <button
                type="button"
                className="piatto"
                disabled={aggiorna.isPending}
                onClick={() => aggiorna.mutate({ active: !utente.active })}
              >
                {utente.active ? 'Disattiva questo accesso' : 'Riattiva questo accesso'}
              </button>
            )}
            {modificabile && !io && utente.active && <Riapertura utente={utente} />}
            {/* 🚨 Sul PROPRIO accesso non si cambia né ruolo né attivazione:
                un proprietario che si declassa si chiude fuori, e se è
                l'ultimo chiude fuori tutti. Lo rifiuta anche il server. */}
            {io && (
              <p className="tenue piccolo">
                Il proprio ruolo e il proprio accesso li cambia un altro proprietario.
              </p>
            )}
            <Guasto errore={aggiorna.error} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * «Ho dimenticato la password» visto dalla parte di chi lo può risolvere.
 *
 * ⭐ È la strada che funziona SEMPRE: non chiede un server di posta, non chiede
 * che la casella di quella persona risponda, non chiede niente. E nessuno
 * sceglie una password per qualcun altro — si toglie quella che c'era, e la
 * persona rientra dalla porta del primo accesso.
 *
 * ⚠️ Chiede conferma perché è un gesto che si vede DA UN'ALTRA PARTE: chi lo
 * subisce viene buttato fuori subito, e se sta lavorando se lo trova addosso
 * senza sapere perché. Non è pericoloso — è solo scortese farlo per sbaglio.
 */
function Riapertura({ utente }: { utente: UtenteStaff }): React.JSX.Element {
  const qc = useQueryClient();
  const [chiedo, setChiedo] = useState(false);
  const [fatto, setFatto] = useState(false);

  const riapri = useMutation({
    meta: { fatto: `accesso di ${utente.email} riaperto` },
    mutationFn: () => chiama('POST', `/settings/users/${utente.id}/reopen-access`),
    onSuccess: () => {
      setChiedo(false);
      setFatto(true);
      void qc.invalidateQueries({ queryKey: ['utenti-staff'] });
    },
  });

  if (fatto) {
    return (
      <p className="riuscito">
        Accesso riaperto. {utente.email} adesso entra dalla schermata di accesso con «È il mio primo accesso»
        e si sceglie la password. Fino ad allora non può entrare: è lo stato voluto, non un guasto.
      </p>
    );
  }

  if (!chiedo) {
    return (
      <button type="button" className="piatto" onClick={() => setChiedo(true)}>
        Riapri l’accesso (password dimenticata)
      </button>
    );
  }

  return (
    <div className="conferma">
      <p className="tenue piccolo">
        A <strong>{utente.email}</strong> viene tolta la password e chiuse tutte le sessioni aperte, anche
        quella da cui sta lavorando adesso. Nessuna password nuova viene creata né mandata a nessuno.
      </p>
      <Guasto errore={riapri.error} />
      <button type="button" disabled={riapri.isPending} onClick={() => riapri.mutate()}>
        {riapri.isPending ? 'Un attimo…' : 'Sì, riapri l’accesso'}
      </button>
      <button type="button" className="piatto" onClick={() => setChiedo(false)}>
        Lascia stare
      </button>
    </div>
  );
}

function Spunte({ utente, modificabile }: { utente: UtenteStaff; modificabile: boolean }): React.JSX.Element {
  const qc = useQueryClient();
  const [scelte, setScelte] = useState<Permesso[]>(utente.permissions);
  const [salvato, setSalvato] = useState(false);

  const salva = useMutation({
    meta: { fatto: `permessi di ${utente.email} salvati` },
    mutationFn: (permissions: Permesso[]) =>
      chiama('POST', `/settings/users/${utente.id}/permissions`, { permissions }),
    onSuccess: () => {
      setSalvato(true);
      void qc.invalidateQueries({ queryKey: ['utenti-staff'] });
    },
  });

  const ha = (p: Permesso) => scelte.includes(p);
  const cambia = (nuove: Permesso[]) => {
    setSalvato(false);
    setScelte(nuove);
  };

  const scavalca = utente.role === 'owner' || utente.role === 'admin';

  return (
    <div className="spunte">
      {scavalca && (
        <p className="tenue piccolo">
          Un <strong>{utente.role}</strong> scavalca la lista: può tutto comunque. Le spunte qui sotto restano
          scritte, e tornano a contare il giorno in cui il ruolo cambia.
        </p>
      )}
      {CAPITOLI.map((c) => (
        <fieldset key={c.capitolo}>
          <legend>
            <label>
              <input
                type="checkbox"
                checked={ha(c.capitolo)}
                disabled={!modificabile || salva.isPending}
                onChange={(e) =>
                  // ⭐ QUI STA LA CURA: il capitolo porta con sé le sue
                  // sottosezioni, in tutti e due i versi.
                  cambia(
                    e.target.checked
                      ? concediCapitolo(scelte, c.capitolo)
                      : togliCapitolo(scelte, c.capitolo),
                  )
                }
              />
              <strong>{c.titolo}</strong>
            </label>
          </legend>
          {c.sottosezioni.map((s) => (
            <label key={s} className="sotto">
              <input
                type="checkbox"
                checked={ha(s)}
                disabled={!modificabile || salva.isPending}
                onChange={(e) =>
                  cambia(
                    e.target.checked
                      ? // Spuntare una sottosezione tira dentro il suo
                        // capitolo: ci pensa `normalizzaPermessi`.
                        normalizzaPermessi([...scelte, s])
                      : scelte.filter((p) => p !== s),
                  )
                }
              />
              {ETICHETTE[s]}
            </label>
          ))}
        </fieldset>
      ))}

      <Guasto errore={salva.error} />
      {salvato && (
        <p className="riuscito">
          Salvato. Si vedrà al prossimo caricamento di quella persona, senza che debba rientrare.
        </p>
      )}
      {modificabile && (
        <button type="button" disabled={salva.isPending} onClick={() => salva.mutate(scelte)}>
          {salva.isPending ? 'Un attimo…' : 'Salva le spunte'}
        </button>
      )}
    </div>
  );
}

function Nuovo(): React.JSX.Element {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [ruolo, setRuolo] = useState<Ruolo>('staff');

  const crea = useMutation({
    meta: { fatto: `accesso aperto per ${nome || email}: la password la sceglie al primo accesso` },
    mutationFn: () =>
      chiama('POST', '/settings/users', {
        email,
        fullName: nome,
        role: ruolo,
        permissions: ['view_dashboard'],
      }),
    onSuccess: () => {
      setEmail('');
      setNome('');
      void qc.invalidateQueries({ queryKey: ['utenti-staff'] });
    },
  });

  return (
    <form
      className="riquadro"
      onSubmit={(e) => {
        e.preventDefault();
        crea.mutate();
      }}
    >
      <h3>Apri un accesso nuovo</h3>
      {/* 📌 Qui NON si scrive una password per qualcun altro: l'operatore se la
          mette lui al primo accesso, e nessuna password passa da questa
          schermata, da una chat o da un foglietto. */}
      <p className="tenue piccolo">
        Nasce senza password: la sceglie l’operatore stesso, dalla schermata di accesso, con «È il mio primo
        accesso».
      </p>
      <Campo
        etichetta="Email"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Campo etichetta="Nome e cognome" required value={nome} onChange={(e) => setNome(e.target.value)} />
      <label className="campo">
        <span>Ruolo</span>
        <select value={ruolo} onChange={(e) => setRuolo(e.target.value as Ruolo)}>
          {RUOLI.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </label>
      <Guasto errore={crea.error} />
      <button type="submit" disabled={crea.isPending}>
        {crea.isPending ? 'Un attimo…' : 'Apri l’accesso'}
      </button>
    </form>
  );
}
